import { cache } from 'react'
import axiosInstance from '@/lib/axios'
import { createSearchParams } from '@/lib/utils'
import { MetaData } from '@/types/meta'
import { SlidesFilterParams, SlidesListResponse } from '@/types/slide'
import { PagesFilterParams, PagesListResponse, PageType } from '@/types/page'
import { CertificatesFilterParams, CertificatesListResponse } from '@/types/certificate'
import { VideosFilterParams, VideosListResponse } from '@/types/video'
import { PhotoGalleriesFilterParams, PhotoGalleriesListResponse, PhotoGalleryType } from '@/types/photo-gallery'
import {
  ProductionStagesFilterParams,
  ProductionStagesListResponse,
  ProductionStageType
} from '@/types/production-stage'
import { CataloguesFilterParams, CataloguesListResponse } from '@/types/catalogue'
import {
  CategoriesFilterParams,
  CategoriesListResponse,
  CategoriesTreeResponse,
  CategoryFilterParams,
  CategoryType
} from '@/types/category'
import { CaptchaParams, CaptchaType } from '@/types/captcha'
import { BlogPostsFilterParams, BlogPostsListResponse, BlogPostType } from '@/types/blog-post'
import { EventsFilterParams, EventsListResponse, EventType } from '@/types/event'
import { ContactUnitsFilterParams, ContactUnitsListResponse } from '@/types/contact-unit'
import { UserType, UpdateProfileParams } from '@/types/user'
import { RegisterParamsType, ForgotPasswordParamsType, ChangePasswordParamsType } from '@/types/auth'
import { SuccessResponseType } from '@/types/general'
import { ProductsFilterParams, ProductsListResponse, ProductType } from '@/types/product'
import { VehicleGroupsFilterParams, VehicleGroupsListResponse } from '@/types/vehicle-group'
import { BrandsFilterParams, BrandsListResponse } from '@/types/brand'
import { ModelsFilterParams, ModelsListResponse } from '@/types/model'
import {
  AddressType,
  AddressesListResponse,
  AddressesFilterParams,
  CreateAddressParams,
  UpdateAddressParams,
  CountryType,
  StateType,
  CityType
} from '@/types/address'
import { CartType, AddCartItemParams, UpdateCartItemParams } from '@/types/cart'
import {
  AccountOrdersFilterParams,
  AccountOrdersListResponse,
  CheckoutParams,
  GuestCheckoutParams,
  OrderTrackingType,
  OrderType
} from '@/types/order'

/**
 * Fetch metadata for SSR with cache
 */
export const fetchMetaData = cache(async (): Promise<MetaData> => {
  const { data } = await axiosInstance.get<MetaData>('/shared/essential/meta')
  return data
})

/**
 * Fetch current user data
 * Axios will automatically read token from cookies (client or server)
 *
 * @returns User data
 */
export const fetchMe = async () => {
  const { data } = await axiosInstance.get('/account/me')
  return data
}

/**
 * Fetch captcha data (client-side only, no cache)
 */
export const fetchCaptcha = async (params?: CaptchaParams): Promise<CaptchaType> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CaptchaType>(`/shared/captcha${searchParams ? `?${searchParams}` : ''}`)
  return data
}

/**
 * Fetch slides for SSR with cache
 */
export const fetchSlides = cache(async (params?: SlidesFilterParams): Promise<SlidesListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<SlidesListResponse>(`/slides${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch pages for SSR with cache
 */
export const fetchPages = cache(async (params?: PagesFilterParams): Promise<PagesListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<PagesListResponse>(`/pages${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch a single page by slug for SSR with cache
 */
export const fetchPageBySlug = cache(async (slug: string): Promise<PageType> => {
  const { data } = await axiosInstance.get<PageType>(`/pages/slug/${slug}`)
  return data
})

/**
 * Fetch contact units for SSR with cache
 */
export const fetchContactUnits = cache(async (params?: ContactUnitsFilterParams): Promise<ContactUnitsListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<ContactUnitsListResponse>(
    `/contact-units${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch certificates for SSR with cache
 */
export const fetchCertificates = cache(async (params?: CertificatesFilterParams): Promise<CertificatesListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CertificatesListResponse>(
    `/certificates${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch videos for SSR with cache
 */
export const fetchVideos = cache(async (params?: VideosFilterParams): Promise<VideosListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<VideosListResponse>(`/videos${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch photo galleries for SSR with cache
 */
export const fetchPhotoGalleries = cache(
  async (params?: PhotoGalleriesFilterParams): Promise<PhotoGalleriesListResponse> => {
    const searchParams = createSearchParams(params).toString()
    const { data } = await axiosInstance.get<PhotoGalleriesListResponse>(
      `/photo-galleries${searchParams ? `?${searchParams}` : ''}`
    )
    return data
  }
)

/**
 * Fetch a single photo gallery by slug for SSR with cache
 */
export const fetchPhotoGalleryBySlug = cache(async (slug: string): Promise<PhotoGalleryType> => {
  const { data } = await axiosInstance.get<PhotoGalleryType>(`/photo-galleries/slug/${slug}`)
  return data
})

/**
 * Fetch production stages for SSR with cache
 */
export const fetchProductionStages = cache(
  async (params?: ProductionStagesFilterParams): Promise<ProductionStagesListResponse> => {
    const searchParams = createSearchParams(params).toString()
    const { data } = await axiosInstance.get<ProductionStagesListResponse>(
      `/production-stages${searchParams ? `?${searchParams}` : ''}`
    )
    return data
  }
)

/**
 * Fetch a single production stage by slug for SSR with cache
 */
export const fetchProductionStageBySlug = cache(async (slug: string): Promise<ProductionStageType> => {
  const { data } = await axiosInstance.get<ProductionStageType>(`/production-stages/slug/${slug}`)
  return data
})

/**
 * Fetch catalogues for SSR with cache
 */
export const fetchCatalogues = cache(async (params?: CataloguesFilterParams): Promise<CataloguesListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CataloguesListResponse>(
    `/catalogues${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch blog posts for SSR with cache
 */
export const fetchBlogPosts = cache(async (params: BlogPostsFilterParams): Promise<BlogPostsListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<BlogPostsListResponse>(
    `/blog-posts${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch a single blog post by slug for SSR with cache
 */
export const fetchBlogPostBySlug = cache(async (slug: string): Promise<BlogPostType> => {
  const { data } = await axiosInstance.get<BlogPostType>(`/blog-posts/slug/${slug}`)
  return data
})

/**
 * Fetch events for SSR with cache
 */
export const fetchEvents = cache(async (params: EventsFilterParams): Promise<EventsListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<EventsListResponse>(`/events${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch a single event by slug for SSR with cache
 */
export const fetchEventBySlug = cache(async (slug: string): Promise<EventType> => {
  const { data } = await axiosInstance.get<EventType>(`/events/slug/${slug}`)
  return data
})

/**
 * Download a media file by ID (client-side only, no cache)
 */
export const downloadMedia = async (fileId: string): Promise<Blob> => {
  const { data } = await axiosInstance.get(`/shared/media/download/${fileId}`, { responseType: 'blob' })
  return data
}

/**
 * Fetch categories for SSR with cache
 */
export const fetchCategories = cache(async (params?: CategoriesFilterParams): Promise<CategoriesListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CategoriesListResponse>(
    `/categories${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch category tree for SSR with cache
 */
export const fetchCategoriesTree = cache(async (params: CategoriesFilterParams): Promise<CategoriesTreeResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CategoriesTreeResponse>(
    `/categories/tree${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch category by id for SSR with cache
 */
export const fetchCategoryById = cache(async (id: string, params?: CategoryFilterParams): Promise<CategoryType> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CategoryType>(`/categories/${id}${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch category by slug for SSR with cache
 */
export const fetchCategoryBySlug = cache(async (slug: string, params?: CategoryFilterParams): Promise<CategoryType> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<CategoryType>(
    `/categories/slug/${slug}${searchParams ? `?${searchParams}` : ''}`
  )
  return data
})

/**
 * Fetch products for SSR with cache
 */
export const fetchProducts = cache(async (params?: ProductsFilterParams): Promise<ProductsListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<ProductsListResponse>(`/products${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch vehicle groups for SSR with cache
 */
export const fetchVehicleGroups = cache(
  async (params?: VehicleGroupsFilterParams): Promise<VehicleGroupsListResponse> => {
    const searchParams = createSearchParams(params).toString()
    const { data } = await axiosInstance.get<VehicleGroupsListResponse>(
      `/vehicle-groups${searchParams ? `?${searchParams}` : ''}`
    )
    return data
  }
)

/**
 * Fetch vehicle group by slug for SSR with cache
 *
 * @param slug VehicleGroup slug
 * @returns VehicleGroup data
 */
export const fetchVehicleGroupBySlug = cache(async (slug: string) => {
  const { data } = await axiosInstance.get(`/vehicle-groups/slug/${slug}`)
  return data
})

/**
 * Fetch brands for SSR with cache
 */
export const fetchBrands = cache(async (params?: BrandsFilterParams): Promise<BrandsListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<BrandsListResponse>(`/brands${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch brand by slug for SSR with cache
 *
 * @param slug Brand slug
 * @returns Brand data
 */
export const fetchBrandBySlug = cache(async (slug: string) => {
  const { data } = await axiosInstance.get(`/brands/slug/${slug}`)
  return data
})

/**
 * Fetch models for SSR with cache
 */
export const fetchModels = cache(async (params?: ModelsFilterParams): Promise<ModelsListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<ModelsListResponse>(`/models${searchParams ? `?${searchParams}` : ''}`)
  return data
})

/**
 * Fetch model by slug for SSR with cache
 *
 * @param slug Model slug
 * @returns Model data
 */
export const fetchModelBySlug = cache(async (slug: string) => {
  const { data } = await axiosInstance.get(`/models/slug/${slug}`)
  return data
})

/**
 * Fetch product by id for SSR with cache
 *
 * @param id Product ID
 * @returns Product data
 */
export const fetchProductById = cache(async (id: string) => {
  const { data } = await axiosInstance.get(`/products/${id}`)
  return data
})

/**
 * Fetch product by slug for SSR with cache
 *
 * @param id Product slug
 * @returns Product data
 */
export const fetchProductBySlug = cache(async (slug: string): Promise<ProductType> => {
  const { data } = await axiosInstance.get(`/products/slug/${slug}`)
  return data
})

export type ContactFormPayload = {
  firstname: string
  lastname: string
  email: string
  phone?: string
  subject: string
  message: string
  captcha?: { key: string; text: string }
}

/**
 * Submit a contact form message
 */
export const submitContactForm = async (payload: ContactFormPayload): Promise<void> => {
  await axiosInstance.post('/forms/contact', payload)
}

export const submitJobApplication = async (payload: FormData): Promise<void> => {
  await axiosInstance.post('/forms/job-application', payload, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

/**
 * Update the current user's profile
 */
export const updateProfile = async (payload: UpdateProfileParams): Promise<UserType> => {
  const { data } = await axiosInstance.patch<UserType>('/account/update', payload)
  return data
}

/**
 * Verify email with a token
 */
export const verifyEmail = async (token: string): Promise<SuccessResponseType> => {
  const { data } = await axiosInstance.get<SuccessResponseType>(`/auth/email-verification/${token}`)
  return data
}

/**
 * Resend email verification
 */
export const resendEmailVerification = async (): Promise<SuccessResponseType> => {
  const { data } = await axiosInstance.post<SuccessResponseType>('/account/resend-email-verification', undefined, {
    skipGlobalErrorToast: true
  })
  return data
}

/**
 * Register a new user
 */
export const register = async (payload: RegisterParamsType): Promise<SuccessResponseType> => {
  const { data } = await axiosInstance.post<SuccessResponseType>('/auth/register', payload)
  return data
}

/**
 * Send forgot password email
 */
export const forgotPassword = async (payload: ForgotPasswordParamsType): Promise<SuccessResponseType> => {
  const { data } = await axiosInstance.post<SuccessResponseType>('/auth/password', payload)
  return data
}

/**
 * Reset password with a token
 */
export const resetPassword = async (token: string, payload: ChangePasswordParamsType): Promise<SuccessResponseType> => {
  const { data } = await axiosInstance.post<SuccessResponseType>(`/auth/password/${token}`, payload)
  return data
}

// ─── Addresses ─────────────────────────────────────────────────────────────

export const listAddresses = async (params?: AddressesFilterParams): Promise<AddressesListResponse> => {
  const { data } = await axiosInstance.get<AddressesListResponse>('/account/addresses', { params })
  return data
}

export const createAddress = async (payload: CreateAddressParams): Promise<AddressType> => {
  const { data } = await axiosInstance.post<AddressType>('/account/addresses', payload)
  return data
}

export const showAddress = async (id: string): Promise<AddressType> => {
  const { data } = await axiosInstance.get<AddressType>(`/account/addresses/${id}`)
  return data
}

export const updateAddress = async (id: string, payload: UpdateAddressParams): Promise<AddressType> => {
  const { data } = await axiosInstance.patch<AddressType>(`/account/addresses/${id}`, payload)
  return data
}

export const deleteAddress = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/account/addresses/${id}`)
}

export const setAddressPrimary = async (id: string): Promise<AddressType> => {
  const { data } = await axiosInstance.patch<AddressType>(`/account/addresses/${id}/set-primary`)
  return data
}

// ─── Geo (Countries / States / Cities) ────────────────────────────────────

export const fetchCountries = async (): Promise<CountryType[]> => {
  const { data } = await axiosInstance.get<CountryType[]>('/shared/essential/countries')
  return data
}

export const fetchStates = async (countryId: string): Promise<StateType[]> => {
  const { data } = await axiosInstance.get<StateType[]>(`/shared/essential/states/${countryId}`)
  return data
}

export const fetchCities = async (stateId: string): Promise<CityType[]> => {
  const { data } = await axiosInstance.get<CityType[]>(`/shared/essential/cities/${stateId}`)
  return data
}

// ─── Cart ────────────────────────────────────────────────────────────────────

export const fetchCart = async (): Promise<CartType> => {
  const { data } = await axiosInstance.get<CartType>('/account/cart')
  return data
}

export const addCartItem = async (payload: AddCartItemParams): Promise<CartType> => {
  const { data } = await axiosInstance.post<CartType>('/account/cart/items', payload)
  return data
}

export const updateCartItem = async (productId: string, payload: UpdateCartItemParams): Promise<CartType> => {
  const { data } = await axiosInstance.patch<CartType>(`/account/cart/items/${productId}`, payload)
  return data
}

export const removeCartItem = async (productId: string): Promise<CartType> => {
  const { data } = await axiosInstance.delete<CartType>(`/account/cart/items/${productId}`)
  return data
}

export const clearCart = async (): Promise<void> => {
  await axiosInstance.delete('/account/cart')
}

export const checkout = async (params: CheckoutParams): Promise<OrderType> => {
  const { data } = await axiosInstance.post<OrderType>('/account/checkout', params)
  return data
}

export const listMyOrders = async (params?: AccountOrdersFilterParams): Promise<AccountOrdersListResponse> => {
  const searchParams = createSearchParams(params).toString()
  const { data } = await axiosInstance.get<AccountOrdersListResponse>(
    `/account/orders${searchParams ? `?${searchParams}` : ''}`
  )
  return data
}

export const fetchMyOrder = async (id: string): Promise<OrderType> => {
  const { data } = await axiosInstance.get<OrderType>(`/account/orders/${id}`)
  return data
}

export const guestCheckout = async (params: GuestCheckoutParams): Promise<OrderType> => {
  const { data } = await axiosInstance.post<OrderType>('/checkout', params)
  return data
}

export const trackOrder = async (id: string): Promise<OrderTrackingType> => {
  const { data } = await axiosInstance.get<OrderTrackingType>(`/orders/track/${id}`)
  return data
}

export const fetchOrderTracking = cache(async (id: string): Promise<OrderTrackingType> => {
  const { data } = await axiosInstance.get<OrderTrackingType>(`/orders/track/${id}`)
  return data
})

export const validateOrderId = async (id: string): Promise<{ id: string }> => {
  const { data } = await axiosInstance.get<{ id: string }>(`/orders/track/lookup/${id}`)
  return data
}
